// src/pages/shrines/AlMukhayamShrine.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const AlMukhayamShrine = () => {
  // Static image URLs
  const mukhayam1 = "https://ziyaratguide.com/wp-content/uploads/2025/09/Al-Mukhayam-Shrine.webp";
  const mukhayam2 = "https://ziyaratguide.com/wp-content/uploads/2025/09/Al-Mukhiyam1.png";
  const mukhayam3 = "https://ziyaratguide.com/wp-content/uploads/2025/09/Al-Mukhiyam2.png";

  return (
    <>
      <Helmet>
        <title>Al-Mukhayam Shrine – Karbala | Ziyarat Guide</title>
        <meta
          name="description"
          content="Discover the Al-Mukhayam Shrine in Karbala, the historic site symbolizing Imam Hussain’s encampment. Learn its history, features, rituals, and visitor tips."
        />
        <meta property="og:title" content="Al-Mukhayam Shrine – Karbala | Ziyarat Guide" />
        <meta
          property="og:description"
          content="Explore the sacred Al-Mukhayam Shrine — the Camp of Imam Hussain (AS). Understand its historical significance, activities, and nearby ziyarat sites."
        />
        <meta property="og:image" content={mukhayam1} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="imam-page">
        {/* Main content */}
        <div className="imam-content">
          <h1 className="page-title">Al Mukhayam Shrine</h1>

          <section className="content-section">
            <p>
              The Al-Mukhayam Shrine (also referred to as The Camp Shrine) in Karbala is a significant
              religious site, holding great historical and spiritual importance for Muslims, especially
              those commemorating the events of Ashura. It serves as a reminder of the encampment of Imam
              Hussain (PBUH) and his family during the tragic events of the Battle of Karbala in 680 CE.
            </p>
            <img src={mukhayam1} alt="Al Mukhayam Shrine 1" className="imam-image" />
          </section>

          <section className="content-section">
            <h2>Historical Significance</h2>
            <ul>
              <li>Marks the approximate location where Imam Hussain’s family and companions camped before the battle.</li>
              <li>Serves as a reminder of the suffering, thirst, and oppression endured by Imam Hussain’s camp.</li>
              <li>Associated with the bravery, patience, and sacrifice of the martyrs of Karbala.</li>
            </ul>
            <img src={mukhayam2} alt="Al Mukhayam Shrine 2" className="imam-image" />
          </section>

          <section className="content-section">
            <h2>Key Features</h2>
            <ul>
              <li><strong>Replica Tents:</strong> Symbolic tents depict the encampment of Imam Hussain and his followers.</li>
              <li><strong>The Tent of Imam Hussain:</strong> Pilgrims recite Ziyarat prayers here.</li>
              <li><strong>The Tent of Hazrat Abbas:</strong> Marks Abbas Ibn Ali’s tent and his sacrifice.</li>
              <li><strong>Children’s Area:</strong> Commemorates the suffering of children, particularly Ali Asghar.</li>
              <li><strong>Sculptures and Artwork:</strong> Calligraphy and artwork depicting Ashura events.</li>
            </ul>
            <img src={mukhayam3} alt="Al Mukhayam Shrine 3" className="imam-image" />
          </section>

          <section className="content-section">
            <h2>Activities at Al-Mukhayam Shrine</h2>
            <ul>
              <li><strong>Mourning Rituals:</strong> Participate in latmiyyah and noha chants during Muharram.</li>
              <li><strong>Educational Tours:</strong> Guided tours explain each tent and location.</li>
              <li><strong>Family Reflection:</strong> Parents teach children lessons of faith, sacrifice, and justice.</li>
              <li><strong>Charity and Giving:</strong> Distribute water, food, and items to honor the camp’s suffering.</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Tips for Visiting</h2>
            <ul>
              <li>Best Times: Muharram and Arbaeen; open year-round.</li>
              <li>Dress Modestly: Respectful attire is required.</li>
              <li>Quiet Reflection: Maintain a calm and respectful demeanor.</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Significance for Families and Religious Pilgrims</h2>
            <p>
              Offers a deeply spiritual experience; provides a solemn environment for prayer, reflection,
              and learning about values upheld by Imam Hussain and his camp: truth, justice, and standing
              against oppression.
            </p>
          </section>

          <section className="content-section">
            <h2>Nearby and Related Sites</h2>
            <ul>
              <li>Tal Al Zainabeiah site</li>
              <li>Beyn al-Haramayn</li>
              <li>Bazars Around the Shrines</li>
              <li>Gold and Jewelry Markets</li>
              <li>Arbaeen Pilgrimage</li>
              <li>Muharram and Ashura Events</li>
              <li>Al-Ukhaidir Fortress</li>
              <li>Kifl Shrine – Burial site of Prophet Ezekiel</li>
              <li>Memory Islam Museum</li>
              <li>The Baron Hotel</li>
              <li>The Baron Karbala</li>
              <li>Coral Karbala Hotel</li>
              <li>Karbala Rayhaan Hotel</li>
            </ul>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="imam-sidebar">
          <h2>Other Shrines</h2>
          <ul>
            <li><Link to="/shrines/shrine-of-imam-hussain">Imam Hussain (PBUH) Shrine</Link></li>
            <li><Link to="/shrines/al-abbas-shrine">Al Abbas (PBUH) Shrine</Link></li>
            <li><Link to="/shrines/al-mukhayam-shrine">Al-Mukhayam Shrine</Link></li>
            <li><Link to="/shrines/shrine-of-two-sons-muslim-ibn-aqeel">Sons of Muslim ibn Aqeel</Link></li>
          </ul>
        </aside>
      </div>
    </>
  );
};

export default AlMukhayamShrine;
